import { Menu } from './FarChat/ChatFeedbackOverlays';
import React from 'react';
import { Badge, Box, IconButton, MenuItem, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import MenuIcon from '@mui/icons-material/Menu';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';

import ColorModeToggle from './ColorModeToggle';
import WaypointLogo from './WaypointLogo';
import { useColorMode } from '../theme/ColorModeContext';
import { GLOBAL_NAVBAR_HEIGHT, GLOBAL_NAVBAR_Z_INDEX } from './globalNavbarLayout';

export interface GlobalNavbarProps {
  userEmail?: string | null;
  accountBadgeCount: number;
  unreadWorkflowNotificationCount: number;
  accountAnchorEl: HTMLElement | null;
  accountButtonRef: React.Ref<HTMLButtonElement>;
  accountMenuItems: React.ReactNode;
  primaryActions?: React.ReactNode;
  onHome: () => void;
  onManageWorkflows: () => void;
  onOpenFeedback: () => void;
  onAccountOpen: (event: React.MouseEvent<HTMLElement>) => void;
  onAccountClose: () => void;
}

const flattenMenuItems = (children: React.ReactNode): React.ReactNode[] =>
  React.Children.toArray(children).flatMap((child) =>
    React.isValidElement<{ children: React.ReactNode }>(child) && child.type === React.Fragment
      ? flattenMenuItems(child.props.children)
      : [child],
  );

const GlobalNavbar: React.FC<GlobalNavbarProps> = ({
  userEmail,
  accountBadgeCount,
  unreadWorkflowNotificationCount,
  accountAnchorEl,
  accountButtonRef,
  accountMenuItems,
  primaryActions,
  onHome,
  onManageWorkflows,
  onOpenFeedback,
  onAccountOpen,
  onAccountClose,
}) => {
  const theme = useTheme();
  const { mode, toggle } = useColorMode();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const isCompactViewport = useMediaQuery(theme.breakpoints.down('md'));
  const menuOpen = Boolean(accountAnchorEl);
  const menuId = isPhone ? 'mobile-navigation-menu' : 'account-navigation-menu';
  const accountLabel = unreadWorkflowNotificationCount > 0
    ? 'Account, workflow notifications pending'
    : 'Account';

  return (
    <>
      <Box
        component="nav"
        aria-label="Global navigation"
        sx={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: GLOBAL_NAVBAR_Z_INDEX,
          height: isPhone ? GLOBAL_NAVBAR_HEIGHT.xs : GLOBAL_NAVBAR_HEIGHT.sm,
          bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider',
          boxShadow: 1, display: 'flex', flexDirection: 'column', minWidth: 0,
        }}
      >
        <Typography
          data-testid="cui-notice-row"
          data-cui-surface="header"
          data-cui-text-size="increased"
          component="span"
          color="success.main"
          sx={{
            minHeight: { xs: 30, sm: 18 }, px: { xs: 1, sm: 3 },
            bgcolor: (appTheme) => appTheme.palette.action.hover,
            borderBottom: 1, borderColor: 'divider', display: 'flex',
            alignItems: 'flex-start', justifyContent: 'center', fontWeight: 700,
            fontSize: { xs: '0.8125rem', sm: '0.875rem' }, lineHeight: 1.2,
            textAlign: 'center', whiteSpace: 'normal', overflowWrap: 'anywhere',
            pointerEvents: 'none',
          }}
        >
          UNCLASSIFIED — CUI NOT AUTHORIZED. Do not enter, paste, upload, or process CUI in Waypoint.
        </Typography>
        <Box
          data-testid="global-header-actions"
          sx={{
            flex: 1, minHeight: 46, width: '100%', px: { xs: 1.5, sm: 2, md: 3 },
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            minWidth: 0,
          }}
        >
          <Tooltip title="Home">
            <IconButton
              data-testid="global-header-logo"
              data-tour-id="navbar-logo"
              data-visual-alignment="lifted-compact"
              aria-label="Home"
              onClick={onHome}
              sx={{ p: 0, borderRadius: 1, flexShrink: 0, transform: 'translateY(-1px)' }}
            >
              <WaypointLogo height={isPhone ? 28 : isCompactViewport ? 32 : 40} />
            </IconButton>
          </Tooltip>
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: { xs: 0, sm: 0.75 },
            minWidth: 0, flexShrink: 0,
          }}>
            {primaryActions}
            <Tooltip title="Manage Workflows" disableInteractive>
              <IconButton aria-label="Manage Workflows" color="primary" size="medium"
                onClick={onManageWorkflows}>
                <FolderOpenIcon />
              </IconButton>
            </Tooltip>
            {!isPhone && (
              <Tooltip title="Feedback">
                <IconButton aria-label="Feedback" data-tour-id="feedback-button"
                  color="primary" size="medium" onClick={onOpenFeedback}>
                  <FeedbackOutlinedIcon />
                </IconButton>
              </Tooltip>
            )}
            {!isPhone && <ColorModeToggle />}
            <Tooltip title={isPhone ? 'Navigation menu' : 'Account'}>
              <IconButton
                ref={accountButtonRef}
                data-tour-id={isPhone ? 'mobile-nav-menu' : undefined}
                aria-label={isPhone ? 'Navigation menu' : accountLabel}
                aria-haspopup="menu"
                aria-expanded={menuOpen ? 'true' : 'false'}
                aria-controls={menuOpen ? menuId : undefined}
                color="primary"
                size="medium"
                onClick={onAccountOpen}
              >
                <Badge color="error" max={99} badgeContent={accountBadgeCount}
                  invisible={accountBadgeCount === 0}>
                  {isPhone ? <MenuIcon /> : <AccountCircleIcon />}
                </Badge>
              </IconButton>
            </Tooltip>
            <Menu
              id={menuId}
              anchorEl={accountAnchorEl}
              open={menuOpen}
              onClose={onAccountClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              sx={{ zIndex: 2100 }}
            >
              <MenuItem disabled>
                <Typography variant="body2">{userEmail || 'Not signed in'}</Typography>
              </MenuItem>
              {flattenMenuItems(accountMenuItems)}
              {isPhone && (
                <MenuItem onClick={() => { toggle(); onAccountClose(); }}>
                  {mode === 'light' ? <Brightness4Icon color="primary" /> : <Brightness7Icon color="primary" />}
                  <Typography component="span" sx={{ ml: 1 }}>
                    {mode === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
                  </Typography>
                </MenuItem>
              )}
              {isPhone && (
                <MenuItem data-tour-id="feedback-button"
                  onClick={() => { onAccountClose(); onOpenFeedback(); }}>
                  <FeedbackOutlinedIcon fontSize="small" />
                  <Typography component="span" sx={{ ml: 1 }}>Feedback</Typography>
                </MenuItem>
              )}
            </Menu>
          </Box>
        </Box>
      </Box>
      <Box sx={{ height: isPhone ? GLOBAL_NAVBAR_HEIGHT.xs : GLOBAL_NAVBAR_HEIGHT.sm }} />
    </>
  );
};

export default GlobalNavbar;
